import NodeCluster from "./NodeCluster";

export default class ClustersGenerator {
    // Public:
    constructor(cParams) {
        this.saveParams(cParams);
        this.computeMastersList();
    }

    compute() {
        this.initClusters();
        this.initNodeClustersProperty();
        const nThreshold = this._nConnectionsThreshold;
        let mastersNodes = this._mastersList;
        mastersNodes.forEach(firstNode => {
            const itHasNotBeenComputed = this.hasNotBeenComputed(firstNode);
            if (itHasNotBeenComputed) {
                let cluster = this.createBlankCluster();
                cluster.add(firstNode);
                const allSharedNumbers = firstNode.sharedNumbers;
                allSharedNumbers.forEach((sharedNumbers, group) => {
                    const theyHaveEnoughConnections = sharedNumbers.length > nThreshold;
                    if (theyHaveEnoughConnections) {
                        const node = this.findMasterNodeByGroup(group);
                        cluster.add(node);
                    }
                });
                cluster.compute();
                this.addCluster(cluster);
            }
        });
        this.mergeClustersWithCommonMasters();
        this.postProcess();
        return this._clusters;
    }

    // Private:
    saveParams(cParams) {
        this._activeNodes = cParams.activeNodes;
        this._inputNodes = cParams.inputNodes;
        this._nConnectionsThreshold = cParams.nConnectionsThreshold;
        this._masterRadiusParams = cParams.masterRadiusParams;
        this._clusterRadiusParams = cParams.clusterRadiusParams;
        this._soloRadiusParams = cParams.soloRadiusParams;
        this._sharedRadiusParams = cParams.sharedRadiusParams;
        this._minimumRadius = cParams.minimumRadius;
        this._nNodesToMergeOrbitals = cParams.nNodesToMergeOrbitals;
    }

    initClusters() {
        this._clusters = [];
        this._computedMasterNumbers = [];
    }

    initNodeClustersProperty() {
        let nodes = this._inputNodes;
        nodes.forEach(node => {
            node.clusters = [];
        });
    }

    createBlankCluster() {
        let cParams = {
            nodes: this._inputNodes,
            masterRadiusParams: this._masterRadiusParams,
            clusterRadiusParams: this._clusterRadiusParams,
            soloRadiusParams: this._soloRadiusParams,
            sharedRadiusParams: this._sharedRadiusParams,
            minimumRadius: this._minimumRadius,
            nNodesToMergeOrbitals: this._nNodesToMergeOrbitals,
        };
        let cluster = new NodeCluster(cParams);
        return cluster;
    }

    findMasterNodeByGroup(group) {
        let requiredNode = undefined;
        const mastersList = this._mastersList;
        mastersList.forEach(node => {
            const itHasGroup = node.group.indexOf(group) !== -1;
            if (itHasGroup) requiredNode = node;
        });
        return requiredNode;
    }

    addCluster(cluster) {
        this._clusters.push(cluster);
        this.addToComputedNumbers(cluster.masterNodes);
    }

    mergeClustersWithCommonMasters() {
        const clusters = this._clusters;
        const clustersMastersList = this.getClustersMastersNumbers();
        const repeatedNumbers = clustersMastersList.getRepeatedValues();
        repeatedNumbers.forEach(masterNumber => {
            let clustersToMerge = [];
            clusters.forEach(cluster => {
                const itContainsThisMaster = cluster.hasThisMasterNumber(masterNumber);
                if (itContainsThisMaster) clustersToMerge.push(cluster);
            });
            this.mergeClusters(clustersToMerge);
        });
    }

    postProcess() {
        let clusters = this._clusters;
        for (let i = 0; i < clusters.length; i++) {
            const cluster = clusters[i];
            cluster.setId(i);
            cluster.setNumberOfClusters(clusters.length);
        }
        this.computeSiblings();
    }

    computeSiblings() {
        let clusters = this._clusters;
        clusters.forEach(clusterA => {
            let siblings = [];
            let sharedNumbers = clusterA.sharedNumbers;
            clusters.forEach(clusterB => {
                sharedNumbers.forEach(sharedNumber => {
                    const isNotItself = clusterA.id !== clusterB.id;
                    const haveCommonNode = clusterB.nodesIndices.indexOf(sharedNumber) !== -1;
                    if (isNotItself && haveCommonNode) {
                        siblings.push(clusterB.id);
                    }
                });
            });
            clusterA.setSiblings(siblings);
        });
    }

    mergeClusters(clustersToMerge) {
        const mastersNodes = this.getClustersMastersNodes(clustersToMerge);
        this.removeClusters(clustersToMerge);
        let cluster = this.createBlankCluster();
        mastersNodes.forEach(node => {
            cluster.add(node);
        });
        cluster.compute();
        this.addCluster(cluster);
    }

    removeClusters(clustersToRemove) {
        const clusters = this._clusters;
        clustersToRemove.forEach(cluster => {
            const index = clusters.indexOf(cluster);
            clusters.splice(index, 1);
        });
    }

    addToComputedNumbers(numbers) {
        let computedMasterNumbers = this._computedMasterNumbers;
        computedMasterNumbers.selfConcat(numbers);
        this._computedMasterNumbers = computedMasterNumbers;
    }

    hasNotBeenComputed(node) {
        const number = node.number;
        const numbers = this._computedMasterNumbers;
        const itIsNotWithinArray = numbers.indexOf(number) === -1;
        if (itIsNotWithinArray) return true;
        else return false;
    }

    computeMastersList() {
        let mastersList = [];
        let nodes = this._activeNodes;
        for (let i = 0; i < nodes.length; i++) {
            const node = nodes[i];
            if (node.type === "master") {
                mastersList.push(node);
            }
        }
        this._mastersList = mastersList;
    }

    getClustersMastersNodes(clusters) {
        let mastersNodes = [];
        clusters.forEach(cluster => {
            const nodes = cluster._masterNodesObjects;
            nodes.forEach(node => {
                const itIsNotIncluded = mastersNodes.indexOf(node) === -1;
                if (itIsNotIncluded) mastersNodes.push(node);
            });
        });
        return mastersNodes;
    }

    getClustersMastersNumbers() {
        let masterNumbers = [];
        const clusters = this._clusters;
        clusters.forEach(cluster => {
            const numbers = cluster.masterNodes;
            masterNumbers.selfConcat(numbers);
        });
        return masterNumbers;
    }
}
